export const SNACKBAR_MESSAGES = {
  FEEBACK_SUBMITTED: 'Thank you for submitting feedback!',
};

export const API_ERROR_MESSAGES = {
  UNKNOWN_ERROR: 'Unknown error. Please try again later.',
  SERVICE_UNAVAILABLE: 'Service is currently unavailable. Please try again later.',
  TOO_MANY_REQUESTS:
    'It looks like our system is receiving too many requests at the moment. Please wait a few moments and try again.',
  MAX_TOKENS_EXCEEDED: 'File size exceeds system processing. Please upload a smaller file.',
  FILE_UPLOAD_FAILED: 'Something went wrong while uploading your file. Please try again.',
  MAX_ATTEMPTS_EXCEEDED:
    'Oops! We couldn’t generate the transcript.Try again or upload a different file',
  BAD_REQUEST_ERROR:
    'There seems to be an issue with the model. Please contact your support team for assistance.',
  MAX_PAGES_LIMIT_EXCEEDED:
    'You have exceeded the maximum file size limit. Please upload a smaller file.',
  CONTEXT_LIMIT_EXCEEDED:
    'Your chat is too large to process. Please reduce the size of the input and try again.',
  GENERIC_ERROR: 'Something went wrong, please try again',
};

export const CHAT_GRETTINGS = {
  LOADING_REGULAR_TEXT: "One moment, please. I'm on it!",
  LOADING_FILE_TEXT: "Hold on a second, I'm summarizing the file for you!",
  WELCOME_TEXT: ['Hi there!', 'How can I assist you today?'],
  GUIDELINES: [
    [
      'Be Clear and Concise',
      ' Use simple language and get straight to the point to help the chatbot understand your request better.',
    ],
    [
      'Avoid Sensitive Information',
      ' Do not share personal, sensitive, or confidential information with the chatbot.',
    ],
    [
      'Do Not Share Client-Based Information',
      ' Avoid discussing or sharing any client-related information.',
    ],
  ],
  NOTES: 'All conversations are logged for quality assurance and service improvement purposes.',
  INPUT_PLACEHOLDER: 'Instruct Halo what to do with the file (e.g. "summarize in 100 words")',
  POST_UPLOAD_PLACEHOLDER: 'Ask questions about any specific topics mentioned in the file',
};

export const FEEDBACK = {
  BAD_FEEDBACKS: [
    'Irrelevant',
    `Didn't follow instructions`,
    'Factually incorrect',
    'Too little information',
    'Other',
  ],
  TOOLTIP: {
    DISLIKE: 'Poor Response',
    COPY: 'Copy',
  },
  ANIMATION_TIME: 800,
};

export const FILE_UPLOAD = {
  DEFAULT_RESPONSE: 'How can I help you? Select from the following or type your request below',
  MAX_SIZE: 5.5 * 1024 * 1000, // 5MB
  SHORTCUTS: [
    { id: 1, label: 'Provide key takeaways', value: 'provide key takeaways', disabled: true },
    { id: 2, label: 'Summarize', value: 'summarize', disabled: false },
  ],
  SIZE_ERROR: 'File size exceeds system processing. Please upload a smaller file.',
};

export const MODEL_DISCLAIMER_TEXT = `Generated content may occasionally be incorrect, misleading, offensive, or biased. Please verify important information.`;

export const AUDIO_FILE_FORMATS = ['.mp3', '.mp4', '.wav'];

export const MODELS = {
  models: {
    Anthropic: [
      {
        id: 'arn:aws:bedrock:{aws_region}:{aws_account_id}:inference-profile/us.anthropic.claude-sonnet-4-20250514-v1:0',
        name: 'Claude 4.0 Sonnet',
        token_limit: 35000,
        description:
          'A hybrid-reasoning AI model optimized for coding tasks and solving complex problems, with improved performance in coding and math compared to previous models',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.doc', value: 'application/msword' },
          {
            label: '.docx',
            value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          { label: '.csv', value: 'text/csv' },
          { label: '.xls', value: 'application/vnd.ms-excel' },
          {
            label: '.xlsx',
            value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          { label: '.html', value: 'text/html' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Apr 2024',
        link: 'https://www.anthropic.com/claude/sonnet',
      },
      {
        id: 'anthropic.claude-3-5-sonnet-20240620-v1:0',
        name: 'Claude 3.5 Sonnet',
        token_limit: 8192,
        description:
          'Offers a versatile language-based tasks requiring knowledge synthesis, analysis, and creative problem-solving across a wide range of domains',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.doc', value: 'application/msword' },
          {
            label: '.docx',
            value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          },
          { label: '.csv', value: 'text/csv' },
          { label: '.xls', value: 'application/vnd.ms-excel' },
          {
            label: '.xlsx',
            value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
          { label: '.html', value: 'text/html' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'June 2022',
        link: 'https://www.anthropic.com/news/claude-3-5-sonnet',
      },
    ],
    Mistral: [
      {
        id: 'mistral.mistral-large-2402-v1:0',
        name: 'Mistral Large 24',
        token_limit: 8192,
        description:
          'Understands and responds to complex prompts with helpful, informative, and creative text formats',
        fileTypeSupport: [
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: '2021',
        link: 'https://mistral.ai/news/pixtral-large',
      },
    ],
    Gemini: [
      {
        id: 'gemini-2.5-pro',
        name: 'Gemini 2.5 Pro',
        token_limit: 65000,
        description: `Google's most advanced AI model, designed for complex reasoning, code generation, and multimodal understanding. It excels in processing large datasets and handling intricate tasks across various domains.`,
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Early 2023',
        link: 'https://ai.google.dev/gemini-api/docs/models',
      },
      {
        id: 'gemini-2.5-flash',
        name: 'Gemini 2.5 Flash',
        token_limit: 65000,
        description:
          'An experimental model optimized for speed and efficiency, suitable for high-volume, low-latency tasks like summarization and categorization',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Early 2023',
        link: 'https://ai.google.dev/gemini-api/docs/models',
      },
      {
        id: 'gemini-2.0-flash',
        name: 'Gemini 2.0 Flash',
        token_limit: 8192,
        description:
          ' Provides high-speed, cost-effective processing of moderate-complexity tasks where low latency and efficiency are paramount.',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Sep 2021',
        link: 'https://cloud.google.com/vertex-ai/generative-ai/docs/models/gemini/2-0-flash',
      },
      {
        id: 'gemini-1.5-pro',
        name: 'Gemini 1.5 Pro',
        token_limit: 8192,
        description:
          'Performs complex analysis and processing of extremely large amounts of information, leveraging its massive context window and advanced reasoning.',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Sep 2021',
        link: 'https://cloud.google.com/vertex-ai/generative-ai/docs/models/gemini/1-5-pro',
      },
    ],
    Meta: [
      {
        id: 'meta.llama3-70b-instruct-v1:0',
        name: 'LLaMA 3 70B',
        token_limit: 2048,
        description:
          'Handles everyday conversational tasks and generating human-like text responses with a balance of accuracy and efficiency.',
        fileTypeSupport: [
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: '2021',
        link: 'https://ollama.com/library/llama3:70b',
      },
      {
        id: 'meta.llama3-8b-instruct-v1:0',
        name: 'LLaMA 3 8B',
        token_limit: 2048,
        description:
          'Tackles complex, open-domain question-answering tasks and generating in-depth, informative responses with a high degree of accuracy.',
        fileTypeSupport: [
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Dec 2022',
        link: 'https://ollama.com/library/llama3:8b',
      },
    ],
    OpenAI: [
      {
        id: 'gpt-4o',
        name: 'GPT-4o',
        token_limit: 4000,
        description:
          'Delivers highly accurate and context-aware responses in complex and nuanced scenarios',
        fileTypeSupport: [
          { label: '.pdf', value: 'application/pdf' },
          { label: '.jpeg', value: 'image/jpeg' },
          { label: '.jpg', value: 'image/jpeg' },
          { label: '.png', value: 'image/png' },
          { label: '.txt', value: 'text/plain' },
          { label: '.md', value: '.md' },
          { label: '.mp3', value: 'audio/mpeg' },
          { label: '.mp4', value: 'video/mp4' },
          { label: '.wav', value: 'audio/wav' },
        ],
        knowledgeCutoff: 'Oct 2023',
        link: 'https://openai.com/index/hello-gpt-4o/',
      },
    ],
  },
};

export const ACCEPTED_FILE_TYPES =
  'application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,text/plain,text/csv,text/html, text/markdown, audio/mpeg,video/mp4';

export const KNOWLEDGE_BASES = [
  {
    id: '148ef792-05a8-43d1-b94d-873a9c909ad4',
    name: 'Sales',
    description: 'Description for Sales',
    metadata: {
      provider: 'postgres',
    },
    created_at: '2024-04-20T09:00:00Z',
    documents_count: 15,
  },
  {
    id: '375ef745-ed3a-4665-80a5-cf54067f8fcc',
    name: 'Service',
    description: 'Description for Service',
    metadata: {
      provider: 'postgres',
    },
    created_at: '2024-03-15T14:30:00Z',
    documents_count: 3,
  },
  {
    id: '8057e09e-68ae-43ca-ac9d-0af879740622',
    name: 'Compliance',
    description: 'Description for Compliance',
    metadata: {
      provider: 'postgres',
    },
    created_at: '2024-02-10T08:45:00Z',
    documents_count: 11,
  },
];
export function GET_POLLING_INTERVAL(fileSizeInBytes: number) {
  const sizeInMB = fileSizeInBytes / (1024 * 1024);

  if (sizeInMB < 0.5) {
    return 500; // 0.5 seconds
  } else if (sizeInMB < 1) {
    return 1000; // 1 second
  } else if (sizeInMB < 5) {
    return 2000; // 2 seconds
  } else if (sizeInMB < 10) {
    return 3000; // 3 seconds
  } else if (sizeInMB < 25) {
    return 5000; // 5 seconds
  } else {
    return 7000; // > 25 MB
  }
}
export const DISPLAY_MESSAGE_MODEL: {
  [key: string]: {
    knowledge_base_select: string;
    knowledge_base_not_select: string;
  };
} = {
  'gpt-4o': {
    knowledge_base_select: 'Deselect Knowledge base to enable uploads',
    knowledge_base_not_select: 'Supports PDF, JPEG, JPG, PNG, TXT, and MD formats',
  },
  'gpt-4': {
    knowledge_base_select: 'Deselect Knowledge base to enable uploads',
    knowledge_base_not_select: 'Supports PDF, JPEG, JPG, PNG, TXT, and MD formats',
  },
  'gpt-35-turbo': {
    knowledge_base_select: 'Deselect Knowledge base for general chat',
    knowledge_base_not_select: 'Model does not support file upload',
  },
  'anthropic.claude-3-5-sonnet-20240620-v1:0': {
    knowledge_base_select: 'Deselect Knowledge base to enable uploads',
    knowledge_base_not_select:
      'Supports PDF, JPEG, JPG, PNG, TXT, DOC, DOCX, CSV, XLS, XLSX, HTML, and MD formats',
  },
  'mistral.mistral-large-2402-v1:0': {
    knowledge_base_select: 'Deselect Knowledge base for general chat',
    knowledge_base_not_select: 'Model does not support file upload',
  },
  'meta.llama3-70b-instruct-v1:0': {
    knowledge_base_select: 'Deselect Knowledge base for general chat',
    knowledge_base_not_select: 'Model does not support file upload',
  },
  'meta.llama3-8b-instruct-v1:0': {
    knowledge_base_select: 'Deselect Knowledge base for general chat',
    knowledge_base_not_select: 'Model does not support file upload',
  },
  'gemini-2.0-flash': {
    knowledge_base_select: 'Deselect Knowledge base to enable uploads',
    knowledge_base_not_select: 'Supports PDF, JPEG, JPG, PNG, TXT, and MD formats',
  },
  'gemini-1.5-pro': {
    knowledge_base_select: 'Deselect Knowledge base to enable uploads',
    knowledge_base_not_select: 'Supports PDF, JPEG, JPG, PNG, TXT, and MD formats',
  },
};

export const CACHE_DURATIONS = {
  SHORT: 1 * 60 * 1000, // 1 min
  MEDIUM: 5 * 60 * 1000, // 5 min
  LONG: 30 * 60 * 1000, // 30 min
  STATIC: 24 * 60 * 60 * 1000, // 24 hours
};
